import { httpClient } from '../httpClient';

export const dpoApi = {
  getStatus: () =>
    httpClient.get('/dpo/status'),

  submitAssessment: (answers) =>
    httpClient.post('/dpo/assessment', { answers }),

  assignDPO: (userId) =>
    httpClient.post('/dpo/assign', { userId }),

  removeDPO: () =>
    httpClient.delete('/dpo/current'),

  getTasks: () =>
    httpClient.get('/dpo/tasks'),

  createTask: (taskData) =>
    httpClient.post('/dpo/tasks', taskData),

  updateTask: (taskId, updates) =>
    httpClient.put(`/dpo/tasks/${taskId}`, updates),

  deleteTask: (taskId) =>
    httpClient.delete(`/dpo/tasks/${taskId}`),

  getHistory: () =>
    httpClient.get('/dpo/history'),

  deleteHistoryEntry: (entryId) =>
    httpClient.delete(`/dpo/history/${entryId}`),

  getResponsibilities: () =>
    httpClient.get('/dpo/responsibilities'),

  getStats: () =>
    httpClient.get('/dpo/stats'),

  // T077-T079 US-4 — ROPA sign-off (FR-S04)
  signoffROPA: (recordId, { opinionText, ifMatchVersionHash } = {}) =>
    httpClient.post(`/dpo/ropa/${recordId}/signoff`, {
      opinionText,
      ...(ifMatchVersionHash ? { ifMatchVersionHash } : {}),
    }),

  // T115 US-8 — cross-border transfer sign-off (FR-S08)
  signoffCrossBorder: (transferId, { legalBasisVerified, legalBasisNotes, evidence } = {}) =>
    httpClient.post(`/dpo/cross-border/${transferId}/signoff`, {
      legalBasisVerified,
      ...(legalBasisNotes ? { legalBasisNotes } : {}),
      ...(evidence ? { evidence } : {}),
    }),

  // T100 US-6 — notification preferences (FR-S07)
  getNotificationPreferences: () =>
    httpClient.get('/dpo/notification-preferences'),

  updateNotificationPreferences: (preferences) =>
    httpClient.put('/dpo/notification-preferences', { preferences }),

  // T141 US-9 — qualifications CRUD
  getQualifications: () =>
    httpClient.get('/dpo/qualifications'),

  createQualification: (data) =>
    httpClient.post('/dpo/qualifications', data),

  updateQualification: (id, data) =>
    httpClient.put(`/dpo/qualifications/${id}`, data),

  deleteQualification: (id) =>
    httpClient.delete(`/dpo/qualifications/${id}`),

  // T147 US-9 — inspector dossier
  getInspectorDossier: ({ periodFrom, periodTo, language } = {}) => {
    const params = new URLSearchParams({ format: 'json' });
    if (periodFrom) params.set('periodFrom', periodFrom);
    if (periodTo) params.set('periodTo', periodTo);
    if (language) params.set('language', language);
    return httpClient.get(`/dpo/inspector-dossier?${params.toString()}`);
  },

  downloadInspectorDossierZip: ({ periodFrom, periodTo } = {}) => {
    const params = new URLSearchParams({ format: 'zip' });
    if (periodFrom) params.set('periodFrom', periodFrom);
    if (periodTo) params.set('periodTo', periodTo);
    return httpClient.get(`/dpo/inspector-dossier?${params.toString()}`, { responseType: 'blob' });
  },

  downloadInspectorDossierPdf: ({ periodFrom, periodTo, language = 'both' } = {}) => {
    const params = new URLSearchParams({ format: 'pdf', language });
    if (periodFrom) params.set('periodFrom', periodFrom);
    if (periodTo) params.set('periodTo', periodTo);
    return httpClient.get(`/dpo/inspector-dossier?${params.toString()}`, { responseType: 'blob' });
  },

  // T137 US-9 — independence
  declareCOI: (data) =>
    httpClient.post('/dpo/independence/declare-coi', data),

  recordAnnualAttestation: (data) =>
    httpClient.post('/dpo/independence/annual-attestation', data),

  // T133 US-9 — appointment v2
  createAppointmentV2: (data) =>
    httpClient.post('/dpo/appointments', data),

  listAppointments: () =>
    httpClient.get('/dpo/appointments'),

  // T175 US-11 — DPO Command Center composite payload (FR-F03 R-11)
  getCommandCenter: () =>
    httpClient.get('/dpo/command-center'),

  // T201-T202 US-12 — annual board report generation (FR-F04 R-14)
  generateAnnualReport: ({ periodFrom, periodTo, language = 'ar', includeHijri = true } = {}) =>
    httpClient.post('/dpo/annual-report/generate', { periodFrom, periodTo, language, includeHijri }),

  getAnnualReportStatus: (jobId) =>
    httpClient.get(`/dpo/annual-report/${jobId}/status`),

  downloadAnnualReport: (jobId) =>
    httpClient.get(`/dpo/annual-report/${jobId}/download`, { responseType: 'blob' }),

  // T165 US-10 — NDGP submission state machine (FR-F02 R-12)
  generateNDGPSubmission: (appointmentId) =>
    httpClient.post('/dpo/ndgp/submissions', appointmentId ? { appointmentId } : {}),

  transitionNDGPSubmission: (subId, { toState, evidence } = {}) =>
    httpClient.post(`/dpo/ndgp/submissions/${subId}/transition`, { toState, evidence }),

  listNDGPSubmissions: ({ currentOnly } = {}) => {
    const params = new URLSearchParams();
    if (currentOnly) params.set('currentOnly', 'true');
    const qs = params.toString();
    return httpClient.get(`/dpo/ndgp/submissions${qs ? `?${qs}` : ''}`);
  },

  downloadNDGPPdf: (subId) =>
    httpClient.get(`/dpo/ndgp/submissions/${subId}/download/pdf`, { responseType: 'blob' }),

  downloadNDGPJson: (subId) =>
    httpClient.get(`/dpo/ndgp/submissions/${subId}/download/json`, { responseType: 'blob' }),

  getNDGPPreflight: () =>
    httpClient.get('/dpo/ndgp/preflight'),

  // T210 US-13 — DPO advisory log (FR-F05)
  createAdvisoryEntry: (data) =>
    httpClient.post('/dpo/advisory', data),

  listAdvisoryEntries: ({ entryType, from: fromDate, to: toDate, page = 1, perPage = 20 } = {}) => {
    const params = new URLSearchParams();
    if (entryType) params.set('entryType', entryType);
    if (fromDate) params.set('from', fromDate);
    if (toDate) params.set('to', toDate);
    params.set('page', String(page));
    params.set('perPage', String(perPage));
    return httpClient.get(`/dpo/advisory?${params.toString()}`);
  },

  updateAdvisoryEntry: (entryId, data) =>
    httpClient.put(`/dpo/advisory/${entryId}`, data),

  // T216 US-14 — multi-entity coverage (FR-F08)
  listEntityCoverages: (dpoAppointmentId) => {
    const params = new URLSearchParams({ dpoAppointmentId: String(dpoAppointmentId) });
    return httpClient.get(`/dpo/multi-entity?${params.toString()}`);
  },

  createEntityCoverage: (data) =>
    httpClient.post('/dpo/multi-entity', data),

  deleteEntityCoverage: (coverageId) =>
    httpClient.delete(`/dpo/multi-entity/${coverageId}`),

  // Spec 027 P4 — FR-027-08: push contact-only update to NDGP without
  // re-submitting the full appointment package.
  createNDGPContactUpdate: (data) =>
    httpClient.post('/dpo/ndgp/submissions/contact-update', data),
};
